import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAcademicYearStatistics } from "../../../services/StatisticsService";
import { useYearFilters, YearFilters } from "./YearFilters";

// Six états de l'étudiant par année universitaire : admis, passant avec crédits,
// redoublants, sortants, exclus, sans décision ; + classement par moyenne.
// Filtres : année universitaire, semestre (moyennes), spécialité et classe.
const AcademicYearStatistics = ({ filters: sharedFilters = null }) => {
  const { t } = useTranslation();
  // Les filtres de la page Statistiques sont partagés : un seul jeu de filtres
  // pour tous les blocs. Sans filtres fournis, le composant garde les siens.
  const own = useYearFilters(!sharedFilters);
  const filters = sharedFilters || own;
  const { yearId } = filters;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadStats = useCallback(() => {
    if (!yearId) return;
    setLoading(true);
    setError("");
    getAcademicYearStatistics(yearId, filters.params)
      .then((res) => setStats(res.data?.data || null))
      .catch((err) => setError(err?.response?.data?.message || t("pages.statistics.yearLoadError")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearId, filters.key]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const cards = stats
    ? [
        { key: "admitted", label: t("pages.statistics.studentsAdmitted"), value: stats.admitted },
        { key: "credits", label: t("pages.statistics.studentsWithCredits"), value: stats.admittedWithCredits },
        { key: "repeating", label: t("pages.statistics.studentsRepeating"), value: stats.repeating },
        { key: "graduated", label: t("pages.statistics.studentsGraduated"), value: stats.graduated },
        { key: "excluded", label: t("pages.statistics.studentsExcluded"), value: stats.excluded },
        { key: "undecided", label: t("pages.statistics.studentsUndecided"), value: stats.undecided },
      ]
    : [];

  return (
    <div className="card mt-3">
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <h5 className="mb-0">{t("pages.statistics.academicYearTitle")}</h5>
        {!sharedFilters && <YearFilters filters={filters} />}
      </div>
      <div className="card-body">
        {error && <div className="alert alert-warning">{error}</div>}
        {loading && <div className="text-center py-3"><div className="spinner-border text-primary" role="status" /></div>}
        {!loading && stats && (
          <>
            <div className="row">
              {cards.map((c) => (
                <div className="col-xl col-sm-6 mb-3" key={c.key}>
                  <div className="border rounded p-3 h-100">
                    <p className="mb-1 text-muted">{c.label}</p>
                    <h3 className="mb-0">{c.value}</h3>
                  </div>
                </div>
              ))}
            </div>
            <h6 className="mt-2">{t("pages.statistics.rankingByAverage")}</h6>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>{t("pages.statistics.rankColumn")}</th>
                    <th>{t("pages.statistics.matriculeColumn")}</th>
                    <th>{t("pages.statistics.studentColumn")}</th>
                    <th>{t("pages.statistics.averageColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.ranking.length ? (
                    stats.ranking.map((r) => (
                      <tr key={r.studentId}>
                        <td>{r.rank}</td>
                        <td>{r.matricule || "-"}</td>
                        <td>{r.name}</td>
                        <td className="fw-medium">{r.average} / 20</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-3 text-muted">{t("pages.statistics.noDataAvailable")}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AcademicYearStatistics;